import Artist from "./artist"
import { ChildComponentName } from "./childComponent"
import { PointVector2D } from "./geometry"
import { lerp, range } from "./lerp"
import aboutPhoto from './resources/about-photo.jpg'

type LayoutElementProps = {
    draw: boolean,
    size: number,
    location: {x: number, y: number},
    brightness: number
}

type CursorStyle = 'auto' | 'none' | 'pointer' | 'move' | 'grab' | 'text';

export type LayoutMessage = 'cameraAccessRequired' | 'microphoneAccessRequired';

type CursorProps = {
    draw: boolean,
    brightness: number,
    style: CursorStyle
}

type ChildComponentProps = {
    draw: boolean,
    size: {x: number, y: number},
    fontSize: number,
    location: {x: number, y: number},
    brightness: number
}

type ExploreBtnProps = {
    draw: boolean,
    size: {x: number, y: number},
    location: {x: number, y: number},
    brightness: number
}

type ScrollProps = {
    draw: boolean,
    size: number,
    circleSize: number,
    lineWidth: number,
    location: {x: number, y: number},
    lineBrightness: number,
    circleBrightness: number,
    state: number
}

type SocialIconProps = {
    size: number,
    location: {x: number, y: number},
    brightness: number
}

type SocialProps = {
    draw: boolean,
    linkedIn: SocialIconProps,
    telegram: SocialIconProps,
    instagram: SocialIconProps,
}

type MessageProps = {
    draw: boolean,
    size: number,
    location: {x: number, y: number},
    brightness: number,
    message?: LayoutMessage,
}

type LayoutProps = {
    border: number,
    cursor: CursorProps,
    logo: LayoutElementProps,
    heart: LayoutElementProps,
    peace: LayoutElementProps,
    hi: LayoutElementProps,
    photo: LayoutElementProps,
    fullScreen: LayoutElementProps,
    exploreBtn: ExploreBtnProps,
    childComponent: ChildComponentProps,
    social: SocialProps,
    scroll: ScrollProps,
    message: MessageProps,
}

type LayoutType = 'greetingDesktop' | 'greetingMobile' | 'aboutDesktop' | 'aboutMobile' | 'cvDesktop' | 'cvMobile' | 'contactsDesktop' | 'contactsMobile' | 'main';
type StateType = "FadeIn" | "FadeOut" | 'Shift' | 'Normal';

export default class Layout{
    artist: Artist;
    ctx: CanvasRenderingContext2D;
    cursor: HTMLCanvasElement | undefined;
    cursorOverride: CursorStyle | null;
    childComponent: {
        setChildComponentBrightness: (childComponentBrightness: number) => void;
        setChildComponentFontSize: (childComponentBrightness: number) => void;
        setChildComponentLocation: (childComponentLocation: {x:number, y:number}) => void;
        setChildComponentSize: (childComponentSize: {x:number, y:number}) => void;
    } | undefined;
    childDiv: HTMLDivElement | null;
    layoutType: LayoutType;
    state: StateType;
    stateChange: boolean;
    changeStarted: number;
    scrollValue: number;
    scrollPosition: PointVector2D;
    scrollDragging: boolean;
    totalLayoutHeight: number;
    maxScroll: number;
    fullScreenButtonOver: boolean;
    exploreButtonOver: boolean; 
    logoOver: boolean;
    linkedInButtonOver: boolean;
    telegramButtonOver: boolean;
    instagramButtonOver: boolean;
    aboutPhoto: HTMLImageElement;


    constructor(
        artist: Artist,
        time: number,
        layoutType: LayoutType,
        childComponent?: {
            setChildComponentBrightness: (childComponentBrightness: number) => void,
            setChildComponentFontSize: (childComponentFontSize: number) => void,
            setChildComponentLocation: (childComponentLocation: {x:number, y:number}) => void,
            setChildComponentSize: (childComponentSize: {x:number, y:number}) => void,
        },
        cursor?: HTMLCanvasElement,
    ) {
        this.artist = artist;
        this.ctx = this.artist.ctx;
        this.childComponent = childComponent;
        this.cursor = cursor;
        this.cursorOverride = null;
        this.layoutType = layoutType;
        this.state = "FadeIn";
        this.stateChange = true;
        this.changeStarted = time;
        this.childDiv = null;
        this.scrollValue = 0;
        this.scrollPosition = new PointVector2D(0, 0);
        this.scrollDragging = false;
        this.totalLayoutHeight = 0;
        this.maxScroll = 0;
        this.fullScreenButtonOver = false;
        this.exploreButtonOver = false;
        this.logoOver = false;
        this.linkedInButtonOver = false;
        this.telegramButtonOver = false;
        this.instagramButtonOver = false;
        this.aboutPhoto = new Image();
        this.aboutPhoto.src = aboutPhoto;
    }

    getBasicProps(): LayoutProps {
        return {
            border: 0,
            cursor: {
                draw: false,
                brightness: 0,
                style: 'auto'
            },
            logo: {
                draw: false,
                size: 0,
                location: {x: 0, y: 0},
                brightness:0
            },
            heart: {
                draw: false,
                size: 0,
                location: {x: 0, y: 0},
                brightness: 0
            },
            peace: {
                draw: false,
                size: 0,
                location: {x: 0, y: 0},
                brightness: 0
            },
            hi: {
                draw: false,
                size: 0,
                location: {x: 0, y: 0},
                brightness: 0,
            },
            photo: {
                draw: false,
                size: 0,
                location: {x: 0, y: 0},
                brightness: 0,
            },
            fullScreen: {
                draw: false,
                size: 0,
                location: {x: 0, y: 0},
                brightness: 0,
            },
            exploreBtn: {
                draw: false,
                size: {x: 0, y: 0},
                location: {x: 0, y: 0},
                brightness: 0,
            },
            childComponent: {
                draw: false,
                size: {x: 0, y: 0},
                fontSize: 0,
                location: {x: 0, y: 0},
                brightness: 0,
            },
            social: {
                draw: false,
                linkedIn: {
                    size: 0,
                    location: {x: 0, y: 0},
                    brightness: 0,
                },
                telegram: {
                    size: 0,
                    location: {x: 0, y: 0},
                    brightness: 0,
                },
                instagram: {
                    size: 0,
                    location: {x: 0, y: 0},
                    brightness: 0,
                }
            },
            scroll: {
                draw: false,
                size: 0,
                location: {x: 0, y: 0},
                circleSize: 0,
                lineWidth: 0,
                lineBrightness: 0,
                circleBrightness: 0,
                state: 0
            },
            message: {
                draw: false,
                size: 0,
                location: {x: 0, y: 0},
                brightness:0,
            },
        }
    }

    landingIntroAnimationPropsUpdate(props: LayoutProps, windowWidth: number, windowHeight: number, time: number) {
        const waitTime = 2500;
        const symbolFadeInTime = 1000;
        const hiFadeInTime = 1000;
        const greetingFadeInTime = 1000;
        const buttonsFadeInTime = 1000;
        if (time - this.changeStarted < waitTime) {
            props.hi.draw = props.photo.draw = false;
            props.logo.size = props.logo.size * 2;
            props.logo.location = {x: windowWidth / 2 - props.logo.size / 2, y: windowHeight / 2 - props.logo.size / 2};
            props.cursor.brightness = 0;
            props.logo.brightness = Math.min(time / (waitTime/3) ,1 - ((time - (waitTime / 3)) / (waitTime/ 2)));
            props.heart.brightness = props.peace.brightness = 0;
            props.hi.brightness = props.photo.brightness = 0;
            props.childComponent.brightness = 0;
            props.social.draw = false;
            props.social.instagram.brightness = props.social.linkedIn.brightness = props.social.telegram.brightness = 0;
            props.fullScreen.brightness = 0;
            props.exploreBtn.brightness = 0;
        } else if (time - this.changeStarted < waitTime + symbolFadeInTime) {
            props.heart.brightness = props.peace.brightness = props.cursor.brightness = (time - this.changeStarted - waitTime) / symbolFadeInTime;
            props.hi.brightness = props.photo.brightness = 0;
            props.childComponent.brightness = 0;
            props.social.draw = false;
            props.social.instagram.brightness = props.social.linkedIn.brightness = props.social.telegram.brightness = 0;
            props.fullScreen.brightness = 0;
            props.exploreBtn.brightness = 0;
        } else if (time - this.changeStarted < waitTime + symbolFadeInTime + hiFadeInTime) {
            props.hi.brightness = props.photo.brightness = (time - this.changeStarted - waitTime - symbolFadeInTime) / hiFadeInTime;
            props.childComponent.brightness = 0;
            props.social.draw = false;
            props.social.instagram.brightness = props.social.linkedIn.brightness = props.social.telegram.brightness = 0;
            props.fullScreen.brightness = 0;
            props.exploreBtn.brightness = 0;
        } else if (time - this.changeStarted < waitTime + symbolFadeInTime + hiFadeInTime + greetingFadeInTime) {
            props.childComponent.brightness = (time - this.changeStarted - waitTime - symbolFadeInTime - hiFadeInTime) / greetingFadeInTime;
            props.social.instagram.brightness = props.social.linkedIn.brightness = props.social.telegram.brightness = props.childComponent.brightness;
            props.fullScreen.brightness = 0;
            props.exploreBtn.brightness = 0;
        } else if (time - this.changeStarted < waitTime + symbolFadeInTime + hiFadeInTime + greetingFadeInTime + buttonsFadeInTime) {
            props.fullScreen.brightness = 
            props.exploreBtn.brightness = (time - this.changeStarted - waitTime - symbolFadeInTime - hiFadeInTime - greetingFadeInTime) / buttonsFadeInTime;
        } else {
            this.state = 'Normal';
            this.stateChange = false;
        }
    }

    landingOutroAnimationPropsUpdate(props: LayoutProps, time: number) {
        const fadeOutTime = 500;
        if (time - this.changeStarted < fadeOutTime) {
            const brightness = 1 - ((time - this.changeStarted) / fadeOutTime);
            props.cursor.brightness = brightness;
            props.logo.brightness = brightness;
            props.heart.brightness = props.peace.brightness = brightness;
            props.hi.brightness = props.photo.brightness = brightness;
            props.childComponent.brightness = brightness;
            props.social.instagram.brightness = props.social.linkedIn.brightness = props.social.telegram.brightness = brightness;
            props.fullScreen.brightness = brightness;
            props.exploreBtn.brightness = brightness;
        } else {
            props.cursor.brightness = 0;
            props.logo.brightness = 0;
            props.heart.brightness = props.peace.brightness = 0;
            props.hi.brightness = props.photo.brightness = 0;
            props.childComponent.brightness = 0;
            props.social.draw = false;
            props.social.instagram.brightness = props.social.linkedIn.brightness = props.social.telegram.brightness = 0;
            props.fullScreen.brightness = 0;
            props.exploreBtn.brightness = 0;
            this.stateChange = false;
        }
    }

    landingShiftAnimationPropsUpdate(props: LayoutProps, time: number) {
        const shiftTime = 1000;
        if (time - this.changeStarted < shiftTime / 2) {
            const brightness = 1 - ((time - this.changeStarted) / (shiftTime / 2));
            props.hi.brightness = props.photo.brightness = brightness;
            props.childComponent.brightness = brightness;
            props.social.instagram.brightness = props.social.linkedIn.brightness = props.social.telegram.brightness = brightness;
            props.exploreBtn.brightness = brightness * (0.4 + (Math.sin(time / 250) + 1) / 4);
        } else if (time - this.changeStarted < shiftTime) {
            const brightness = ((time - this.changeStarted - (shiftTime / 2)) / (shiftTime / 2));
            props.hi.brightness = props.photo.brightness = brightness;
            props.childComponent.brightness = brightness;
            props.social.instagram.brightness = props.social.linkedIn.brightness = props.social.telegram.brightness = brightness;
            props.exploreBtn.brightness = brightness * (0.4 + (Math.sin(time / 250) + 1) / 4);
        } else {
            this.stateChange = false;
            this.state = 'Normal';
        }
    }

    getLandingDesktopLayoutProps(component: ChildComponentName, windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        const baseSize = 80;
        const symbolBrightnessMultiplyer = 0.75;
        const props = this.getBasicProps();
        props.logo.draw = true;
        props.heart.draw = true;
        props.peace.draw = true;
        props.childComponent.draw = true;
        props.fullScreen.draw = true;
        
        props.heart.size = props.peace.size = props.fullScreen.size = baseSize * 0.8;
        props.border = 20;
        props.childComponent.size = {x: windowWidth >= 600 + props.border * 2 ? 600: windowWidth - props.border * 2, y: 0}
        props.childComponent.fontSize = 25;
        props.heart.brightness = props.peace.brightness = 1;
        props.fullScreen.brightness = 1;
        props.childComponent.brightness = 1;
        
        if (this.cursorOverride) {
            props.cursor.draw = false;
            props.cursor.style = this.cursorOverride;
        } else {
            props.cursor.draw = true;
            props.cursor.brightness = 1;
        }
        
        let mainBlockSize;
        if (component == 'Greeting') {
            props.hi.draw = true;
            props.exploreBtn.draw = true;
            props.hi.size = baseSize * 2;
            props.exploreBtn.size = {x: Math.min(props.childComponent.size.x, 300), y: baseSize * 0.8};
            props.hi.brightness = 1;
            props.exploreBtn.brightness = 1;
            mainBlockSize = props.hi.size + (this.childDiv? this.childDiv.clientHeight : 0) + props.border * 3 + props.exploreBtn.size.y;

            props.hi.location = {x: windowWidth / 2 - props.hi.size / 2, y: windowHeight / 2 - mainBlockSize / 2};
            props.childComponent.location = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2 + props.hi.size};
            props.exploreBtn.location = {x: windowWidth / 2 - props.exploreBtn.size.x / 2, y: windowHeight / 2 + mainBlockSize / 2 - props.exploreBtn.size.y};

            this.exploreButtonOver = (
                cursorX >= props.exploreBtn.location.x && 
                cursorX <= props.exploreBtn.location.x + props.exploreBtn.size.x &&
                cursorY >= props.exploreBtn.location.y &&
                cursorY <= props.exploreBtn.location.y + props.exploreBtn.size.y && 
                this.state === 'Normal'
            )
            
            const exploreBrightnessMultiplyer = this.exploreButtonOver ? 1 : 0.4 + (Math.sin(time / 250) + 1) / 4;
            props.exploreBtn.brightness = props.exploreBtn.brightness * exploreBrightnessMultiplyer;
        } else if (component == 'About') {
            props.photo.draw = true;
            props.photo.size = props.childComponent.size.x;
            props.photo.brightness = 1;
            mainBlockSize = props.photo.size + props.border + (this.childDiv? this.childDiv.clientHeight : 0);
            props.photo.location = {x: windowWidth / 2 - props.photo.size / 2, y: windowHeight / 2 - mainBlockSize / 2};
            props.childComponent.location = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2 + props.photo.size + props.border};
        } else if (component == 'Contacts') {
            const socialLogoMaxSize = baseSize * 1.25;
            const socialFullSize = props.childComponent.size.x;
            const socialLogoSize = socialFullSize >= socialLogoMaxSize * 3 + props.border * 4 ? socialLogoMaxSize : (socialFullSize - props.border * 4) / 3;
            props.childComponent.size.y = props.border * 4 + socialLogoSize;
            mainBlockSize = (this.childDiv? this.childDiv.clientHeight : 0);
            props.childComponent.location = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2};
            props.social.draw = true;
            const socialCenterLocation = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2};
            let headingHight = 0;
            if (this.childDiv?.children[0] && this.childDiv?.children[1]) {
                headingHight += this.childDiv?.children[0].clientHeight + this.childDiv?.children[1].clientHeight;
            }
            props.social.linkedIn.brightness = props.social.telegram.brightness = props.social.instagram.brightness = 1;
            props.social.linkedIn.size = props.social.telegram.size = props.social.instagram.size = socialLogoSize;
            props.social.linkedIn.location = {x: socialCenterLocation.x - socialFullSize / 2, y: socialCenterLocation.y + headingHight + props.border * 3};
            props.social.telegram.location = {x: socialCenterLocation.x - socialLogoSize / 2 - socialLogoSize * 0.02, y: socialCenterLocation.y + headingHight + props.border * 3};
            props.social.instagram.location = {x: socialCenterLocation.x - socialLogoSize + socialFullSize / 2, y: socialCenterLocation.y + headingHight + props.border * 3};
        } else {
            mainBlockSize = (this.childDiv? this.childDiv.clientHeight : 0);
            props.childComponent.location = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2};
        }

        this.totalLayoutHeight = windowHeight / 2 + mainBlockSize / 2 + props.border + props.heart.size + props.border;
        if (this.totalLayoutHeight > windowHeight) {
            return this.getLandingDesktopWithScrollLayoutProps(component, windowWidth, windowHeight, time, cursorX, cursorY);
        }
        
        props.logo.location = {x: props.border, y: props.border};
        props.logo.brightness = 0;
        props.logo.size = baseSize;

        if (this.stateChange && this.state === "FadeIn") {
            this.landingIntroAnimationPropsUpdate(props, windowWidth, windowHeight, time);
        } else if (this.state === "FadeOut") {
            this.landingOutroAnimationPropsUpdate(props, time);
        } else if (this.stateChange && this.state === "Shift") {
            this.landingShiftAnimationPropsUpdate(props, time);
        }
        
        props.heart.location = {x: windowWidth - props.border * 2 - props.heart.size * 2, y: windowHeight - props.border - props.heart.size}
        props.peace.location = {x: windowWidth - props.border -  props.peace.size, y: windowHeight - props.border -  props.peace.size}
        props.fullScreen.location = {x: windowWidth - props.border -  props.fullScreen.size, y: props.border};
        
        this.fullScreenButtonOver = (
            cursorX >= props.fullScreen.location.x && 
            cursorX <= props.fullScreen.location.x + props.fullScreen.size &&
            cursorY >= props.fullScreen.location.y &&
            cursorY <= props.fullScreen.location.y + props.fullScreen.size && 
            this.state === 'Normal'
        )
        const fullScreenBrightnessMultiplyer = this.fullScreenButtonOver ? 1 : document.fullscreenElement? 0.5 : 0.4 + (Math.sin(time / 250) + 1) / 4;
        this.instagramButtonOver = (
            cursorX >= props.social.instagram.location.x && 
            cursorX <= props.social.instagram.location.x + props.social.instagram.size &&
            cursorY >= props.social.instagram.location.y &&
            cursorY <= props.social.instagram.location.y + props.social.instagram.size && 
            this.state === 'Normal'
        )
        const instagramBrightnessMultiplyer = this.instagramButtonOver ? 1 : 0.6;
        this.telegramButtonOver = (
            cursorX >= props.social.telegram.location.x && 
            cursorX <= props.social.telegram.location.x + props.social.telegram.size &&
            cursorY >= props.social.telegram.location.y &&
            cursorY <= props.social.telegram.location.y + props.social.telegram.size && 
            this.state === 'Normal'
        )
        const telegramBrightnessMultiplyer = this.telegramButtonOver ? 1 : 0.6;
        this.linkedInButtonOver = (
            cursorX >= props.social.linkedIn.location.x && 
            cursorX <= props.social.linkedIn.location.x + props.social.linkedIn.size &&
            cursorY >= props.social.linkedIn.location.y &&
            cursorY <= props.social.linkedIn.location.y + props.social.linkedIn.size && 
            this.state === 'Normal'
        )
        const linkedInBrightnessMultiplyer = this.linkedInButtonOver ? 1 : 0.6;
        
        props.logo.brightness = Math.max(props.logo.brightness, props.heart.brightness) * symbolBrightnessMultiplyer;
        props.heart.brightness = props.heart.brightness * symbolBrightnessMultiplyer;
        props.peace.brightness = props.peace.brightness * symbolBrightnessMultiplyer;
        props.fullScreen.brightness = props.fullScreen.brightness * fullScreenBrightnessMultiplyer;
        props.social.linkedIn.brightness = props.social.linkedIn.brightness * linkedInBrightnessMultiplyer;
        props.social.telegram.brightness = props.social.telegram.brightness * telegramBrightnessMultiplyer;
        props.social.instagram.brightness = props.social.instagram.brightness * instagramBrightnessMultiplyer;
        return props
    }

    getLandingDesktopWithScrollLayoutProps(component: ChildComponentName, windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        const baseSize = 80;
        const symbolBrightnessMultiplyer = 0.75;
        const props = this.getBasicProps();
        props.logo.draw = true;
        props.heart.draw = true;
        props.peace.draw = true;
        props.childComponent.draw = true;
        props.scroll.draw = true;
        props.fullScreen.draw = true;
        
        props.heart.size = props.peace.size = props.fullScreen.size = baseSize * 0.8;
        props.border = 20;
        props.childComponent.size = {x: windowWidth >= 600 + props.border * 3 ? 600: windowWidth - props.border * 3, y: 0}
        props.childComponent.fontSize = 25;
        props.heart.brightness = props.peace.brightness = 1;
        props.fullScreen.brightness = 1;
        props.childComponent.brightness = 1;

        if (this.cursorOverride) {
            props.cursor.draw = false;
            props.cursor.style = this.cursorOverride;
        } else {
            props.cursor.draw = true;
            props.cursor.brightness = 1;
        }

        let mainBlockSize;
        if (component == 'Greeting') {
            props.hi.draw = true;
            props.exploreBtn.draw = true;
            props.hi.size = baseSize * 2;
            props.exploreBtn.size = {x: Math.min(props.childComponent.size.x, 300), y: baseSize * 0.8};
            props.hi.brightness = 1;
            props.exploreBtn.brightness = 1;
            mainBlockSize = props.hi.size + (this.childDiv? this.childDiv.clientHeight : 0) + props.border * 3 + props.exploreBtn.size.y;
        } else if (component == 'About') {
            props.photo.draw = true;
            props.photo.size = windowWidth >= 600 + props.border * 2 ? 600: windowWidth - props.border * 2;
            props.photo.brightness = 1;
            mainBlockSize = props.photo.size + props.border + (this.childDiv? this.childDiv.clientHeight : 0);
        } else if (component == 'Contacts') {
            const socialLogoMaxSize = baseSize * 1.25;
            const socialFullSize = props.childComponent.size.x;
            const socialLogoSize = socialFullSize >= socialLogoMaxSize * 3 + props.border * 4 ? socialLogoMaxSize : (socialFullSize - props.border * 4) / 3;
            props.childComponent.size.y = props.border * 4 + socialLogoSize;
            mainBlockSize = (this.childDiv? this.childDiv.clientHeight : 0);
            props.childComponent.location = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2};
            props.social.draw = true;
            const socialCenterLocation = {x: windowWidth / 2 - props.border / 2, y: props.border + baseSize + props.border};
            let headingHight = 0;
            if (this.childDiv?.children[0] && this.childDiv?.children[1]) {
                headingHight += this.childDiv?.children[0].clientHeight + this.childDiv?.children[1].clientHeight;
            }
            props.social.linkedIn.brightness = props.social.telegram.brightness = props.social.instagram.brightness = 1;
            props.social.linkedIn.size = props.social.telegram.size = props.social.instagram.size = socialLogoSize;
            props.social.linkedIn.location = {x: socialCenterLocation.x - socialFullSize / 2, y: socialCenterLocation.y + headingHight + props.border * 3};
            props.social.telegram.location = {x: socialCenterLocation.x - socialLogoSize / 2 - socialLogoSize * 0.02, y: socialCenterLocation.y + headingHight + props.border * 3};
            props.social.instagram.location = {x: socialCenterLocation.x - socialLogoSize + socialFullSize / 2, y: socialCenterLocation.y + headingHight + props.border * 3};
        } else {
            mainBlockSize = (this.childDiv? this.childDiv.clientHeight : 0);
        }
        this.totalLayoutHeight = props.border + baseSize + props.border + mainBlockSize + props.border + props.heart.size + props.border;
        
        const symbolGrowthScroll = props.heart.size * 4;
        this.maxScroll = - this.totalLayoutHeight + windowHeight - symbolGrowthScroll;
        const scrollAmount = this.maxScroll * this.scrollValue;
        const symbolGrowth = scrollAmount > - this.totalLayoutHeight + windowHeight ? 0 : 1 + ((this.maxScroll - scrollAmount) / symbolGrowthScroll);

        props.social.linkedIn.location.y += scrollAmount;
        props.social.telegram.location.y += scrollAmount;
        props.social.instagram.location.y += scrollAmount;
        
        props.heart.size = props.peace.size = lerp(props.heart.size, props.heart.size * 2, symbolGrowth);
        props.heart.location.x = lerp(windowWidth - props.border * 3 - props.heart.size * 2, windowWidth/2 - props.heart.size - props.border/2, symbolGrowth);
        props.peace.location.x = lerp(windowWidth - props.border * 2 - props.peace.size, windowWidth/2 + props.border/2, symbolGrowth);
        props.heart.location.y = props.peace.location.y = lerp(this.totalLayoutHeight - props.border - props.heart.size + scrollAmount, this.totalLayoutHeight - props.border - props.heart.size + scrollAmount + symbolGrowthScroll / 2, symbolGrowth);
        
        props.logo.location = {x: props.border, y: props.border + scrollAmount};
        props.logo.brightness = 0;
        props.logo.size = baseSize;
        
        if (this.stateChange && this.state === "FadeIn") {
            this.landingIntroAnimationPropsUpdate(props, windowWidth, windowHeight, time);
        } else if (this.state === "FadeOut") {
            this.landingOutroAnimationPropsUpdate(props, time);
        } else if (this.stateChange && this.state === "Shift") {
            this.landingShiftAnimationPropsUpdate(props, time);
        }
        props.logo.brightness = Math.max(props.logo.brightness, props.heart.brightness) * symbolBrightnessMultiplyer;
        props.heart.brightness = props.heart.brightness * symbolBrightnessMultiplyer;
        props.peace.brightness = props.peace.brightness * symbolBrightnessMultiplyer;
        //props.social.linkedIn.brightness = props.social.telegram.brightness = props.social.instagram.brightness = props.social.linkedIn.brightness * 0.6;

        props.fullScreen.location = {x: windowWidth - props.border * 2 -  props.fullScreen.size, y: props.border + scrollAmount};
        
        this.fullScreenButtonOver = (
            cursorX >= props.fullScreen.location.x && 
            cursorX <= props.fullScreen.location.x + props.fullScreen.size &&
            cursorY >= props.fullScreen.location.y &&
            cursorY <= props.fullScreen.location.y + props.fullScreen.size && 
            this.state === 'Normal'
        )
        const fullScreenBrightnessMultiplyer = this.fullScreenButtonOver ? 1 : document.fullscreenElement? 0.5 : 0.4 + (Math.sin(time / 250) + 1) / 4;
        props.fullScreen.brightness = props.fullScreen.brightness * fullScreenBrightnessMultiplyer;
        this.instagramButtonOver = (
            cursorX >= props.social.instagram.location.x && 
            cursorX <= props.social.instagram.location.x + props.social.instagram.size &&
            cursorY >= props.social.instagram.location.y &&
            cursorY <= props.social.instagram.location.y + props.social.instagram.size && 
            this.state === 'Normal'
        )
        const instagramBrightnessMultiplyer = this.instagramButtonOver ? 1 : 0.6;
        this.telegramButtonOver = (
            cursorX >= props.social.telegram.location.x && 
            cursorX <= props.social.telegram.location.x + props.social.telegram.size &&
            cursorY >= props.social.telegram.location.y &&
            cursorY <= props.social.telegram.location.y + props.social.telegram.size && 
            this.state === 'Normal'
        )
        const telegramBrightnessMultiplyer = this.telegramButtonOver ? 1 : 0.6;
        this.linkedInButtonOver = (
            cursorX >= props.social.linkedIn.location.x && 
            cursorX <= props.social.linkedIn.location.x + props.social.linkedIn.size &&
            cursorY >= props.social.linkedIn.location.y &&
            cursorY <= props.social.linkedIn.location.y + props.social.linkedIn.size && 
            this.state === 'Normal'
        )
        const linkedInBrightnessMultiplyer = this.linkedInButtonOver ? 1 : 0.6;
        props.social.linkedIn.brightness = props.social.linkedIn.brightness * linkedInBrightnessMultiplyer;
        props.social.telegram.brightness = props.social.telegram.brightness * telegramBrightnessMultiplyer;
        props.social.instagram.brightness = props.social.instagram.brightness * instagramBrightnessMultiplyer;

        if (component == 'Greeting') {
            props.childComponent.location = {x: windowWidth / 2 - props.border / 2, y: props.border + props.logo.size + props.border + props.hi.size + scrollAmount};
            props.exploreBtn.location = {x: windowWidth / 2 - props.exploreBtn.size.x / 2, y: props.border + props.logo.size + props.border + mainBlockSize - props.exploreBtn.size.y + scrollAmount};
            props.hi.location = {x: windowWidth / 2 - props.hi.size / 2, y: props.border + props.logo.size + props.border + scrollAmount};
            this.exploreButtonOver = (
                cursorX >= props.exploreBtn.location.x && 
                cursorX <= props.exploreBtn.location.x + props.exploreBtn.size.x &&
                cursorY >= props.exploreBtn.location.y &&
                cursorY <= props.exploreBtn.location.y + props.exploreBtn.size.y && 
                this.state === 'Normal'
            )
            const exploreBrightnessMultiplyer = this.exploreButtonOver ? 1 : 0.4 + (Math.sin(time / 250) + 1) / 4;
            props.exploreBtn.brightness = props.exploreBtn.brightness * exploreBrightnessMultiplyer;
        } else if (component == 'About') {
            props.childComponent.location = {x: windowWidth / 2 - props.border / 2, y: props.border + props.logo.size + props.border + props.photo.size + props.border + scrollAmount};
            props.photo.location = {x: windowWidth / 2 - props.photo.size / 2, y: props.border + props.logo.size + props.border + scrollAmount};
        } else {
            props.childComponent.location = {x: windowWidth / 2 - props.border / 2, y: props.border + props.logo.size + props.border + scrollAmount};
        }

        props.scroll.size = windowHeight - props.border * 2;
        props.scroll.circleSize = 6;
        props.scroll.lineWidth = 3;
        props.scroll.location = {x: windowWidth - props.border, y: props.border};
        props.scroll.lineBrightness = 0.4 * props.heart.brightness;
        props.scroll.circleBrightness = 1 * props.heart.brightness;
        props.scroll.state = this.scrollValue;
        
        return props
    }

    getLandingMobileNoScrollLayoutProps(component: ChildComponentName, windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        let baseSize = 80;
        const symbolBrightnessMultiplyer = 0.75;
        const props = this.getBasicProps();
        props.logo.draw = true;
        props.heart.draw = true;
        props.peace.draw = true;
        props.childComponent.draw = true;
        
        props.heart.size = props.peace.size = baseSize * 0.8;
        props.border = 20;
        props.heart.brightness = props.peace.brightness = 1;
        props.childComponent.brightness = 1;
        
        let mainBlockSize;
        if (component == 'Greeting') {
            props.hi.draw = true;
            props.hi.size = baseSize * 2;
            props.hi.brightness = 1;
            this.totalLayoutHeight = props.border + baseSize + props.border + props.hi.size + (this.childDiv? this.childDiv.clientHeight : 0) + props.heart.size + props.border;
            if (this.totalLayoutHeight > windowHeight) {
                baseSize = 60;
                props.heart.size = props.peace.size = baseSize * 0.8;
                props.hi.size = baseSize * 2;
                this.totalLayoutHeight = props.border + baseSize + props.border + props.hi.size + (this.childDiv? this.childDiv.clientHeight : 0) + props.heart.size + props.border;
            }
            mainBlockSize = props.hi.size + (this.childDiv? this.childDiv.clientHeight : 0);
            props.hi.location = {x: windowWidth / 2 - props.hi.size / 2, y: windowHeight / 2 - mainBlockSize / 2}
            props.childComponent.location = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2 + props.hi.size};
        } else if (component == 'About') {
            props.photo.draw = true;
            props.photo.size = windowWidth >= 600 + props.border * 2 ? 600: windowWidth - props.border * 2;
            props.photo.brightness = 1;
            this.totalLayoutHeight = props.border + baseSize + props.border + props.photo.size + props.border + (this.childDiv? this.childDiv.clientHeight : 0) + props.heart.size + props.border;
            if (this.totalLayoutHeight > windowHeight) {
                baseSize = 60;
                props.heart.size = props.peace.size = baseSize * 0.8;
                props.photo.size = windowWidth >= 600 + props.border * 2 ? 600: windowWidth - props.border * 2;
                this.totalLayoutHeight = props.border + baseSize + props.border + props.photo.size + props.border + (this.childDiv? this.childDiv.clientHeight : 0) + props.heart.size + props.border;
            }
            mainBlockSize = props.photo.size + props.border + (this.childDiv? this.childDiv.clientHeight : 0);
            props.photo.location = {x: windowWidth / 2 - props.photo.size / 2, y: windowHeight / 2 - mainBlockSize / 2}
            props.childComponent.location = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2 + props.photo.size + props.border};
        } else if (component == 'Contacts') {
            const socialLogoMaxSize = baseSize * 1.25;
            const socialFullSize = windowWidth >= 600 + props.border * 3 ? 600: windowWidth - props.border * 3;
            const socialLogoSize = socialFullSize >= socialLogoMaxSize * 3 + props.border * 4 ? socialLogoMaxSize : (socialFullSize - props.border * 4) / 3;
            props.childComponent.size.y = props.border * 4 + socialLogoSize;
            mainBlockSize = (this.childDiv? this.childDiv.clientHeight : 0);
            props.childComponent.location = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2};
            props.social.draw = true;
            const socialCenterLocation = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2};
            let headingHight = 0;
            if (this.childDiv?.children[0] && this.childDiv?.children[1]) {
                headingHight += this.childDiv?.children[0].clientHeight + this.childDiv?.children[1].clientHeight;
            }
            props.social.linkedIn.brightness = props.social.telegram.brightness = props.social.instagram.brightness = 1;
            props.social.linkedIn.size = props.social.telegram.size = props.social.instagram.size = socialLogoSize;
            props.social.linkedIn.location = {x: socialCenterLocation.x - socialFullSize / 2, y: socialCenterLocation.y + headingHight + props.border * 3};
            props.social.telegram.location = {x: socialCenterLocation.x - socialLogoSize / 2 - socialLogoSize * 0.02, y: socialCenterLocation.y + headingHight + props.border * 3};
            props.social.instagram.location = {x: socialCenterLocation.x - socialLogoSize + socialFullSize / 2, y: socialCenterLocation.y + headingHight + props.border * 3};
            this.totalLayoutHeight = props.border + baseSize + props.border + (this.childDiv? this.childDiv.clientHeight : 0) + props.heart.size + props.border;
            if (this.totalLayoutHeight > windowHeight) {
                baseSize = 60;
                props.heart.size = props.peace.size = baseSize * 0.8;
                props.hi.size = baseSize * 2;
                this.totalLayoutHeight = props.border + baseSize + props.border + (this.childDiv? this.childDiv.clientHeight : 0) + props.heart.size + props.border;
            }
        } else {
            this.totalLayoutHeight = props.border + baseSize + props.border + (this.childDiv? this.childDiv.clientHeight : 0) + props.heart.size + props.border;
            if (this.totalLayoutHeight > windowHeight) {
                baseSize = 60;
                props.heart.size = props.peace.size = baseSize * 0.8;
                props.hi.size = baseSize * 2;
                this.totalLayoutHeight = props.border + baseSize + props.border + (this.childDiv? this.childDiv.clientHeight : 0) + props.heart.size + props.border;
            }
            mainBlockSize = (this.childDiv? this.childDiv.clientHeight : 0);
            props.childComponent.location = {x: windowWidth / 2, y: windowHeight / 2 - mainBlockSize / 2};
        }

        props.logo.location = {x: props.border, y: props.border};
        props.logo.brightness = 0;
        props.logo.size = baseSize;

        if (this.stateChange && this.state === "FadeIn") {
            this.landingIntroAnimationPropsUpdate(props, windowWidth, windowHeight, time);
        } else if (this.stateChange && this.state === "Shift") {
            this.landingShiftAnimationPropsUpdate(props, time);
        }
        props.logo.brightness = Math.max(props.logo.brightness, props.heart.brightness) * symbolBrightnessMultiplyer;
        props.heart.brightness = props.heart.brightness * symbolBrightnessMultiplyer;
        props.peace.brightness = props.peace.brightness * symbolBrightnessMultiplyer;

        this.instagramButtonOver = (
            cursorX >= props.social.instagram.location.x && 
            cursorX <= props.social.instagram.location.x + props.social.instagram.size &&
            cursorY >= props.social.instagram.location.y &&
            cursorY <= props.social.instagram.location.y + props.social.instagram.size && 
            this.state === 'Normal'
        )
        this.telegramButtonOver = (
            cursorX >= props.social.telegram.location.x && 
            cursorX <= props.social.telegram.location.x + props.social.telegram.size &&
            cursorY >= props.social.telegram.location.y &&
            cursorY <= props.social.telegram.location.y + props.social.telegram.size && 
            this.state === 'Normal'
        )
        this.linkedInButtonOver = (
            cursorX >= props.social.linkedIn.location.x && 
            cursorX <= props.social.linkedIn.location.x + props.social.linkedIn.size &&
            cursorY >= props.social.linkedIn.location.y &&
            cursorY <= props.social.linkedIn.location.y + props.social.linkedIn.size && 
            this.state === 'Normal'
        )

        props.heart.location = {x: windowWidth - props.border * 2 - props.heart.size * 2, y: windowHeight - props.border - props.heart.size}
        props.peace.location = {x: windowWidth - props.border -  props.peace.size, y: windowHeight - props.border -  props.peace.size}

        props.childComponent.size.x = windowWidth >= 600 + props.border * 2 ? 600: windowWidth - props.border * 2;
        props.childComponent.fontSize = 25;

        return props
    }

    getLandingMobileLayoutProps(component: ChildComponentName, windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        const baseSize = 60;
        const symbolBrightnessMultiplyer = 0.75;
        const props = this.getBasicProps();
        props.logo.draw = true;
        props.heart.draw = true;
        props.peace.draw = true;
        props.childComponent.draw = true;
        props.scroll.draw = true;
        
        props.heart.size = props.peace.size = baseSize * 0.8;
        props.border = 20;
        props.heart.brightness = props.peace.brightness = 1;
        props.childComponent.brightness = 1;
        
        if (component == 'Greeting') {
            props.hi.draw = true;
            props.hi.size = baseSize * 2;
            props.hi.brightness = 1;
            this.totalLayoutHeight = props.border + baseSize + props.border + props.hi.size + (this.childDiv? this.childDiv.clientHeight : 0) + props.border + props.heart.size + props.border;
        } else if (component == 'About') {
            props.photo.draw = true;
            props.photo.size = windowWidth >= 600 + props.border * 3 ? 600: windowWidth - props.border * 3;
            props.photo.brightness = 1;
            this.totalLayoutHeight = props.border + baseSize + props.border + props.photo.size + props.border + (this.childDiv? this.childDiv.clientHeight : 0) + props.border + props.heart.size + props.border;
        } else if (component == 'Contacts') {
            const socialLogoMaxSize = baseSize * 1.25;
            const socialFullSize = windowWidth >= 600 + props.border * 3 ? 600: windowWidth - props.border * 3;
            const socialLogoSize = socialFullSize >= socialLogoMaxSize * 3 + props.border * 4 ? socialLogoMaxSize : (socialFullSize - props.border * 4) / 3;
            props.childComponent.size.y = props.border * 4 + socialLogoSize;
            const mainBlockSize = (this.childDiv? this.childDiv.clientHeight : 0);
            props.social.draw = true;
            const socialCenterLocation = {x: windowWidth / 2 - props.border / 2, y: props.border + baseSize + props.border};
            let headingHight = 0;
            if (this.childDiv?.children[0] && this.childDiv?.children[1]) {
                headingHight += this.childDiv?.children[0].clientHeight + this.childDiv?.children[1].clientHeight;
            }
            props.social.linkedIn.brightness = props.social.telegram.brightness = props.social.instagram.brightness = 1;
            props.social.linkedIn.size = props.social.telegram.size = props.social.instagram.size = socialLogoSize;
            props.social.linkedIn.location = {x: socialCenterLocation.x - socialFullSize / 2, y: socialCenterLocation.y + headingHight + props.border * 3};
            props.social.telegram.location = {x: socialCenterLocation.x - socialLogoSize / 2 - socialLogoSize * 0.02, y: socialCenterLocation.y + headingHight + props.border * 3};
            props.social.instagram.location = {x: socialCenterLocation.x - socialLogoSize + socialFullSize / 2, y: socialCenterLocation.y + headingHight + props.border * 3};
            this.totalLayoutHeight = props.border + baseSize + props.border + (this.childDiv? this.childDiv.clientHeight : 0) + props.border + props.heart.size + props.border;
        } else {
            this.totalLayoutHeight = props.border + baseSize + props.border + (this.childDiv? this.childDiv.clientHeight : 0) + props.border + props.heart.size + props.border;
        }

        const symbolGrowthScroll = props.heart.size * 4;
        this.maxScroll = - this.totalLayoutHeight + windowHeight - symbolGrowthScroll;

        if (this.maxScroll >= - symbolGrowthScroll) {
            return this.getLandingMobileNoScrollLayoutProps(component, windowWidth, windowHeight, time, cursorX, cursorY);
        }

        const scrollAmount = this.maxScroll * this.scrollValue;
        const symbolGrowth = scrollAmount > - this.totalLayoutHeight + windowHeight ? 0 : 1 + ((this.maxScroll - scrollAmount) / symbolGrowthScroll);
        
        props.social.linkedIn.location.y += scrollAmount;
        props.social.telegram.location.y += scrollAmount;
        props.social.instagram.location.y += scrollAmount;

        props.heart.size = props.peace.size = lerp(props.heart.size, props.heart.size * 2, symbolGrowth);
        props.heart.location.x = lerp(windowWidth - props.border * 3 - props.heart.size * 2, windowWidth/2 - props.heart.size - props.border/2, symbolGrowth);
        props.peace.location.x = lerp(windowWidth - props.border * 2 - props.peace.size, windowWidth/2 + props.border/2, symbolGrowth);
        props.heart.location.y = props.peace.location.y = lerp(this.totalLayoutHeight - props.border - props.heart.size + scrollAmount, this.totalLayoutHeight - props.border - props.heart.size + scrollAmount + symbolGrowthScroll / 2, symbolGrowth);
        
        props.logo.location = {x: props.border, y: props.border + scrollAmount};
        props.logo.brightness = 0;
        props.logo.size = baseSize;

        if (this.stateChange && this.state === "FadeIn") {
            this.landingIntroAnimationPropsUpdate(props, windowWidth, windowHeight, time);
        } else if (this.stateChange && this.state === "Shift") {
            this.landingShiftAnimationPropsUpdate(props, time);
        }

        props.logo.brightness = Math.max(props.logo.brightness, props.heart.brightness) * symbolBrightnessMultiplyer;
        props.heart.brightness = props.heart.brightness * symbolBrightnessMultiplyer;
        props.peace.brightness = props.peace.brightness * symbolBrightnessMultiplyer;

        if (component == 'Greeting') {
            props.hi.location = {x: windowWidth / 2 - props.hi.size / 2, y: props.border + props.logo.size + props.border + scrollAmount};
            props.childComponent.location = {x: windowWidth / 2 - props.border / 2, y: props.border + props.logo.size + props.border + props.hi.size + scrollAmount};
        } else if (component == 'About') {
            props.photo.location = {x: windowWidth / 2 - props.photo.size / 2 - props.border / 2, y: props.border + props.logo.size + props.border + scrollAmount};
            props.childComponent.location = {x: windowWidth / 2 - props.border / 2, y: props.border + props.logo.size + props.border + props.photo.size + props.border + scrollAmount};
        } else {
            props.childComponent.location = {x: windowWidth / 2 - props.border / 2, y: props.border + props.logo.size + props.border + scrollAmount};
        }

        this.instagramButtonOver = (
            cursorX >= props.social.instagram.location.x && 
            cursorX <= props.social.instagram.location.x + props.social.instagram.size &&
            cursorY >= props.social.instagram.location.y &&
            cursorY <= props.social.instagram.location.y + props.social.instagram.size && 
            this.state === 'Normal'
        )
        this.telegramButtonOver = (
            cursorX >= props.social.telegram.location.x && 
            cursorX <= props.social.telegram.location.x + props.social.telegram.size &&
            cursorY >= props.social.telegram.location.y &&
            cursorY <= props.social.telegram.location.y + props.social.telegram.size && 
            this.state === 'Normal'
        )
        this.linkedInButtonOver = (
            cursorX >= props.social.linkedIn.location.x && 
            cursorX <= props.social.linkedIn.location.x + props.social.linkedIn.size &&
            cursorY >= props.social.linkedIn.location.y &&
            cursorY <= props.social.linkedIn.location.y + props.social.linkedIn.size && 
            this.state === 'Normal'
        )

        props.childComponent.size.x = windowWidth >= 600 + props.border * 3 ? 600: windowWidth - props.border * 3;
        props.childComponent.fontSize = 25;

        props.scroll.size = windowHeight - props.border * 2;
        props.scroll.circleSize = 6;
        props.scroll.lineWidth = 2;
        props.scroll.location = {x: windowWidth - props.border, y: props.border};
        props.scroll.lineBrightness = 0.4 * props.heart.brightness;
        props.scroll.circleBrightness = 1 * props.heart.brightness;
        props.scroll.state = this.scrollValue;

        return props
    }

    getGreetingDesktopLayoutProps(windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        return this.getLandingDesktopLayoutProps('Greeting', windowWidth, windowHeight, time, cursorX, cursorY);
    }

    getGreetingMobileLayoutProps(windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        return this.getLandingMobileLayoutProps('Greeting', windowWidth, windowHeight, time, cursorX, cursorY);
    }

    getAboutDesktopLayoutProps(windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        return this.getLandingDesktopLayoutProps('About', windowWidth, windowHeight, time, cursorX, cursorY);
    }

    getAboutMobileLayoutProps(windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        return this.getLandingMobileLayoutProps('About', windowWidth, windowHeight, time, cursorX, cursorY);
    }

    getCvDesktopLayoutProps(windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        return this.getLandingDesktopLayoutProps('CV', windowWidth, windowHeight, time, cursorX, cursorY);
    }

    getCvMobileLayoutProps(windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        return this.getLandingMobileLayoutProps('CV', windowWidth, windowHeight, time, cursorX, cursorY);
    }

    getContactsDesktopLayoutProps(windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        return this.getLandingDesktopLayoutProps('Contacts', windowWidth, windowHeight, time, cursorX, cursorY);
    }

    getContactsMobileLayoutProps(windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number): LayoutProps {
        return this.getLandingMobileLayoutProps('Contacts', windowWidth, windowHeight, time, cursorX, cursorY);
    }

    mainIntroAnimationPropsUpdate(props: LayoutProps, time: number) {
        const fadeInTime = 1000;
        if (time - this.changeStarted < fadeInTime) {
            props.fullScreen.brightness = 
            props.logo.brightness = 
            props.cursor.brightness = 
            props.message.brightness = 
            (time - this.changeStarted) / fadeInTime;
        } else {
            this.state = 'Normal';
            this.stateChange = false;
        }
    }

    mainOutroAnimationPropsUpdate(props: LayoutProps, time: number) {
        const fadeOutTime = 500;
        if (time - this.changeStarted <= fadeOutTime) {
            props.fullScreen.brightness =
            props.logo.brightness =
            props.cursor.brightness =
            props.message.brightness = 
            1 - ((time - this.changeStarted) / fadeOutTime);
        } else {
            props.fullScreen.brightness =
            props.logo.brightness =
            props.cursor.brightness =
            props.message.brightness = 
            0;
        }
    }

    getMainDesctopLayoutProps(windowWidth: number, windowHeight: number, time: number, cursorX: number, cursorY: number, message?: LayoutMessage): LayoutProps {
        const baseSize = 80;
        const symbolBrightnessMultiplyer = 0.75;
        const props = this.getBasicProps();
        props.cursor.draw = true;
        props.logo.draw = true;
        props.fullScreen.draw = true;
        props.message.draw = true;
        
        props.border = 20;
        props.logo.brightness = 1;
        props.logo.location = {x: props.border, y: props.border};
        props.logo.size = baseSize;
        
        props.cursor.brightness = 1;
        
        props.fullScreen.brightness = 1;
        props.fullScreen.size = baseSize * 0.8;
        props.fullScreen.location = {x: windowWidth - props.border -  props.fullScreen.size, y: props.border};

        if (message) {
            props.message.message = message;
            props.message.brightness = 1;
            props.message.size = baseSize;
            props.message.location = {x: windowWidth/2, y: windowHeight/2};
        }

        if (this.stateChange && this.state === "FadeIn") {
            this.mainIntroAnimationPropsUpdate(props, time);
        } else if (this.state === "FadeOut") {
            this.mainOutroAnimationPropsUpdate(props, time);
        }
        
        this.fullScreenButtonOver = (
            cursorX >= props.fullScreen.location.x && 
            cursorX <= props.fullScreen.location.x + props.fullScreen.size &&
            cursorY >= props.fullScreen.location.y &&
            cursorY <= props.fullScreen.location.y + props.fullScreen.size && 
            this.state === 'Normal'
        )
        this.logoOver = (
            cursorX >= props.logo.location.x && 
            cursorX <= props.logo.location.x + props.logo.size &&
            cursorY >= props.logo.location.y &&
            cursorY <= props.logo.location.y + props.logo.size && 
            this.state === 'Normal'
        )
        const fullScreenBrightnessMultiplyer = this.fullScreenButtonOver ? 1 : 0.5;
        const logoBrightnessMultiplyer = this.logoOver? 1 : 0.5
        
        props.logo.brightness = Math.max(props.logo.brightness, props.heart.brightness) * logoBrightnessMultiplyer;
        props.fullScreen.brightness = props.fullScreen.brightness * fullScreenBrightnessMultiplyer;

        return props;
    }

    setLayoutType(layoutType: LayoutType) {
        this.layoutType = layoutType;
    }

    drawLogo({location, size, brightness}: LayoutElementProps) {
        const hiP1 = new Path2D("M 0 0 L 190 0 L 300 455 L 300 710 L 360 710 L 396 860 L 300 860 L 300 1000 L 0 1000 Z");
        const hiP2 = new Path2D("M 225 0 L 534 0 L 700 691 L 700 0 L 1000 0 L 1000 1000 L 466 1000 Z");
        const hi = new Path2D();
        let m = new DOMMatrix();
        m.a = size / 1000; m.b = 0; m.c = 0;
        m.d = size / 1000; m.e = location.x; m.f = location.y;
        hi.addPath(hiP1, m);
        hi.addPath(hiP2, m);
        this.ctx.fillStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        this.ctx.fill(hi);
    }

    drawHeart({location, size, brightness}: LayoutElementProps) {
        this.ctx.fillStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        const h = new Path2D("M12 4.248c-3.148-5.402-12-3.825-12 2.944 0 4.661 5.571 9.427 12 15.808 6.43-6.381 12-11.147 12-15.808 0-6.792-8.875-8.306-12-2.944z");
        const heart = new Path2D();
        let m = new DOMMatrix();
        m.a = 4.15 / 100 * size; m.b = 0; m.c = 0;
        m.d = 4.3 / 100 * size; m.e = location.x; m.f = location.y;
        heart.addPath(h, m);
        this.ctx.fill(heart);
    }

    drawHi({location, size, brightness}: LayoutElementProps) {
        const lineWidth = size / 20;
        this.ctx.strokeStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        this.ctx.lineJoin = "bevel";
        this.ctx.lineWidth = lineWidth;
        const hiP1 = new Path2D("M297.527,276.524c-15.01,25.872-87.23,64.183-70.937-6.275,5.737-24.811,29.064-113.615,29.064-113.615,0,0-53.656,95.772-77.614,126.826-31.596,40.954-62.752,34.679-51.853-18.495,8.527-41.601,54.569-159.669,72.991-212.367,18.165-51.964,62.755-74.088,36.991-4.624-17.394,46.899-33.959,67.439-74.583,107.733C80.098,236.531,10.138,230.768,2.994,283.46c-3.105,22.899,8.697,37.872,28.073,16.183C50.837,277.515,150.627.964,150.627.964c0,0-54.826,131.23-122.202,155.67");
        const hiP2 = new Path2D("M279.764,86.616s-7.266,16.675-14.532,41.615");
        const hiP3 = new Path2D("M408.572.964s-31.046,75.744-50.202,207.193");
        const hiP4 = new Path2D("M353.104,253.735s-3.688,19.156-7.377,59.229");
        const hi = new Path2D();
        let m = new DOMMatrix();
        m.a = 0.24 / 100 * size; m.b = 0; m.c = 0;
        m.d = 0.24 / 100 * size; m.e = location.x; m.f = location.y;
        hi.addPath(hiP1, m);
        hi.addPath(hiP2, m);
        hi.addPath(hiP3, m);
        hi.addPath(hiP4, m);
        this.ctx.stroke(hi);
    }

    drawPhoto({location, size, brightness}: LayoutElementProps) {
        //this.ctx.filter = "grayscale(50%)";
        this.ctx.filter = `brightness(${brightness})`;
        this.ctx.drawImage(this.aboutPhoto, location.x, location.y, size, size);
        this.ctx.filter = `brightness(1)`;
    }

    drawPeace({location, size, brightness}: LayoutElementProps) {
        const lineWidth = size / 14;
        this.ctx.strokeStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        this.ctx.lineWidth = lineWidth;
        this.ctx.beginPath();
        this.ctx.arc(location.x + size/2, location.y + size/2, (size - lineWidth) / 2, 0, Math.PI * 2); // Outer circle
        this.ctx.stroke();
        this.ctx.closePath();
        this.ctx.moveTo(location.x + size/2, location.y);
        this.ctx.lineTo(location.x + size/2, location.y + size);
        this.ctx.stroke();
        this.ctx.moveTo(location.x + size/2, location.y + size/2);
        this.ctx.lineTo(location.x + size/6, location.y + size/1.2);
        this.ctx.stroke();
        this.ctx.moveTo(location.x + size/2, location.y + size/2);
        this.ctx.lineTo(location.x + size - size/6, location.y + size/1.2);
        this.ctx.stroke();
    }

    drawScroll({location, size, circleSize, lineWidth, lineBrightness, circleBrightness, state}: ScrollProps) {
        this.ctx.strokeStyle = `rgb(${255 * lineBrightness} ${255 * lineBrightness} ${255 * lineBrightness})`;
        this.ctx.lineWidth = lineWidth;
        const circleRadius = circleSize;
        this.ctx.beginPath()
        this.ctx.moveTo(location.x, location.y);
        this.ctx.lineTo(location.x, location.y + size);
        this.ctx.stroke();
        this.ctx.beginPath()
        this.ctx.fillStyle = `rgb(0 0 0)`;
        this.ctx.arc(location.x, location.y + size * state, circleRadius, 0, 2 * Math.PI);
        this.ctx.fill();
        this.ctx.beginPath()
        this.ctx.lineWidth = lineWidth;
        this.ctx.strokeStyle = `rgb(${255 * circleBrightness} ${255 * circleBrightness} ${255 * circleBrightness})`;
        this.ctx.arc(location.x, location.y + size * state, circleRadius, 0, 2 * Math.PI);
        this.ctx.stroke();
        this.scrollPosition.set(location.x, location.y + size * state);
    }

    drawCursor({draw, brightness, style}: CursorProps) {
        if (this.cursor) {
            if (!this.cursorOverride) {
                this.cursor.style.cursor = "none";
                this.cursor.style.opacity = brightness.toString();
            } else {
                this.cursor.style.cursor = style;
                this.cursor.style.opacity = '0';
            }
        }
    }

    drawGreeting({size, fontSize, location, brightness}: ChildComponentProps) {
        if (this.childComponent) {
            this.childComponent.setChildComponentSize(size);
            this.childComponent.setChildComponentFontSize(fontSize);
            this.childComponent.setChildComponentLocation(location);
            this.childComponent.setChildComponentBrightness(brightness);
        }
    }

    drawFullscreen({location, size, brightness}: LayoutElementProps) {
        this.ctx.fillStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        //const fs = new Path2D("M382.68,800h417.32v-417.32h-103.1v314.23h-314.23M416.69,0H0v416.69h103.09V103.09h313.6V0ZM624.38,80.59L80.59,624.38,0,543.79v256.21h256.21l-80.59-80.59L719.41,175.62l80.59,80.59V0h-256.21l80.59,80.59Z");
        //const fs = new Path2D('M453.54,793.7h340.16v-340.16h-70.87v269.29h-269.29M340.16,0H0v340.16h70.87V70.87h269.29V0ZM675.31,71.91L72.89,674.34l-2.02-135.75H0v255.12h255.12v-70.87l-133.35-.94L722.87,120.8l-.03,134.32h70.87V0h-255.12v70.87l136.73,1.05Z');
        const fullScreen = new Path2D();
        let m = new DOMMatrix();
        m.a = 1.25 / 1000 * size; m.b = 0; m.c = 0;
        m.d = 1.25 / 1000 * size; m.e = location.x; m.f = location.y;
        if(!document.fullscreenElement){ 
            const fs = new Path2D("M510.24,793.7h283.46v-283.46h-70.87v212.6h-212.6M283.46,0H0v283.46h70.87V70.87h212.6V0ZM675.31,71.91L72.89,674.34l-2.02-164.1H0v283.46h283.46v-70.87l-161.7-.94L722.87,120.8l-.03,162.67h70.87V0h-283.46v70.87l165.07,1.05Z");
            fullScreen.addPath(fs, m);
        } else { 
            const fs = new Path2D('M113.39,396.85h283.46v283.46h-70.87l.03-162.67L49.96,793.7H0v-46.48l278.46-278.46-165.07-1.05v-70.87ZM747.22,0l-277.49,277.49-2.02-164.1h-70.87v283.46h283.46v-70.87l-161.7-.94L793.7,49.96V0h-46.48ZM70.87,70.87h212.6V0H0v283.46h70.87V70.87ZM722.83,510.24v212.6h-212.6v70.87h283.46v-283.46h-70.87Z');
            fullScreen.addPath(fs, m);
        }
        this.ctx.fill(fullScreen);
    }

    drawExploreBtn({location, size, brightness}: ExploreBtnProps) {
        this.ctx.strokeStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        this.ctx.lineWidth = size.y / 14;
        const { x: xPos, y: yPos } = location;
        const { x: width, y: height } = size;
        const radius = height / 2;
        this.ctx.beginPath();
        this.ctx.moveTo(xPos + radius, yPos);
        this.ctx.lineTo(xPos + width - radius, yPos);
        this.ctx.arcTo(xPos + width, yPos, xPos + width, yPos + radius, radius);
        this.ctx.lineTo(xPos + width, yPos + height - radius);
        this.ctx.arcTo(xPos + width, yPos + height, xPos + width - radius, yPos + height, radius);
        this.ctx.lineTo(xPos + radius, yPos + height);
        this.ctx.arcTo(xPos, yPos + height, xPos, yPos + height - radius, radius);
        this.ctx.lineTo(xPos, yPos + radius);
        this.ctx.arcTo(xPos, yPos, xPos + radius, yPos, radius);
        this.ctx.closePath();
        this.ctx.stroke();
        this.ctx.font = `${size.y * 0.65}px Roboto Condensed`;
        this.ctx.textAlign = "center";
        this.ctx.textBaseline = "middle";
        this.ctx.fillStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        this.ctx.fillText("EXPLORE >", location.x + size.x / 2, location.y + size.y / 1.8);
    }

    drawTelegram({location, size, brightness}: SocialIconProps) {
        this.ctx.fillStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        const smt = new Path2D("M91.51,0c-1.45.06-2.77.48-3.94.94-1.08.42-4.94,2.05-11.1,4.63-6.15,2.58-14.31,6-23,9.66-17.38,7.31-36.92,15.54-46.93,19.75-.34.14-1.74.57-3.28,1.75-1.55,1.18-3.26,3.74-3.26,6.54,0,2.26,1.13,4.56,2.49,5.88s2.74,1.93,3.9,2.39h0c3.9,1.54,15.55,6.21,18.32,7.32.98,2.95,5.21,15.57,6.21,18.74h0c.71,2.25,1.4,3.71,2.36,4.93.48.61,1.05,1.16,1.76,1.6.27.17.57.3.87.42.04.02.08.02.13.04l-.12-.03c.09.04.17.09.25.12.14.05.24.05.44.09.69.21,1.37.35,2,.35,2.72,0,4.39-1.47,4.39-1.47l.11-.08,11.74-9.93,14.38,13.3c.26.37,2.32,3.15,7.08,3.15,2.84,0,5.09-1.41,6.52-2.88,1.44-1.47,2.33-2.98,2.73-5.03h0c.32-1.66,14.08-70.77,14.08-70.77l-.03.12c.43-1.91.55-3.76.05-5.65s-1.87-3.72-3.52-4.69S92.95-.05,91.51,0ZM89.53,10.96c-.54,2.73-12.87,64.67-13.63,68.55l-20.76-19.21-14.04,11.87,3.89-15.2s26.82-27.14,28.43-28.72c1.3-1.27,1.57-1.71,1.57-2.15,0-.59-.3-1.01-1-1.01-.63,0-1.47.6-1.92.88-5.72,3.57-30.08,17.45-42.06,24.26-.72-.29-11.54-4.64-17.38-6.96,10.37-4.37,28.15-11.85,44.71-18.81,8.69-3.66,16.84-7.08,22.99-9.66,5.19-2.18,7.9-3.3,9.19-3.84ZM75.75,80.22h0s0,.03,0,.03c0-.03,0,0,0-.03Z");
        const telegram = new Path2D();
        let m = new DOMMatrix();
        m.a = 1. / 100 * size; m.b = 0; m.c = 0;
        m.d = 1. / 100 * size; m.e = location.x; m.f = location.y;
        telegram.addPath(smt, m);
        this.ctx.fill(telegram);
    }

    drawLinkedIn({location, size, brightness}: SocialIconProps) {
        this.ctx.fillStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        const smli = new Path2D("M88.65,0H11.08C4.96,0,0,5.03,0,11.24v78.69c0,6.21,4.96,11.24,11.08,11.24h77.57c6.12,0,11.08-5.03,11.08-11.24V11.24c0-6.21-4.96-11.24-11.08-11.24ZM33.24,78.69h-13.98v-39.35h13.98v39.35ZM26.01,32.14c-4.27,0-7.13-2.89-7.13-6.75s2.85-6.75,7.6-6.75c4.27,0,7.13,2.89,7.13,6.75s-2.85,6.75-7.6,6.75ZM83.11,78.69h-13.53v-21.51c0-5.95-3.61-7.32-4.96-7.32s-5.86.92-5.86,7.32v21.51h-13.98v-39.35h13.98v5.49c1.8-3.2,5.41-5.49,12.17-5.49s12.18,5.49,12.18,17.84v21.51Z");
        const linkedIn = new Path2D();
        let m = new DOMMatrix();
        m.a = 1. / 100 * size; m.b = 0; m.c = 0;
        m.d = 1. / 100 * size; m.e = location.x; m.f = location.y;
        linkedIn.addPath(smli, m);
        this.ctx.fill(linkedIn);
    }

    drawInstagram({location, size, brightness}: SocialIconProps) {
        this.ctx.fillStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        const smi = new Path2D("M27.8,0C12.4,0,0,12.4,0,27.8v44.4c0,15.3,12.4,27.8,27.8,27.8h44.4c15.3,0,27.8-12.4,27.8-27.8V27.8c0-15.4-12.4-27.8-27.8-27.8H27.8ZM83.3,11.1c3.1,0,5.6,2.5,5.6,5.6s-2.5,5.6-5.6,5.6-5.6-2.5-5.6-5.6,2.5-5.6,5.6-5.6ZM50,22.2c15.3,0,27.8,12.4,27.8,27.8s-12.4,27.8-27.8,27.8-27.8-12.4-27.8-27.8,12.4-27.8,27.8-27.8ZM50,33.3c-9.2,0-16.7,7.5-16.7,16.7s7.5,16.7,16.7,16.7,16.7-7.5,16.7-16.7-7.5-16.7-16.7-16.7Z");
        const instagram = new Path2D();
        let m = new DOMMatrix();
        m.a = 1. / 100 * size; m.b = 0; m.c = 0;
        m.d = 1. / 100 * size; m.e = location.x; m.f = location.y;
        instagram.addPath(smi, m);
        this.ctx.fill(instagram);
    }

    drawMessage({draw, location, size, brightness, message}: MessageProps) {
        if (message) {
            switch(message) {
                case "cameraAccessRequired":
                    this.drawCameraMessage({draw, location, size, brightness});
                    break;
                case "microphoneAccessRequired":
                    this.drawMicrophoneMessage({draw, location, size, brightness});
                    break;
            }
        }
    }

    drawCameraMessage({location, size, brightness}: LayoutElementProps) {
        this.ctx.font = `${size}px Roboto Condensed`;
        this.ctx.textAlign = "center";
        this.ctx.textBaseline = "middle";
        this.ctx.fillStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        const text1 = 'PLEASE ACTIVATE';
        const text2 = 'YOUR CAMERA';
        const text1Y = location.y - size / 2;
        const text2Y = location.y + size / 2;
        this.ctx.fillText(text1, location.x, text1Y);
        this.ctx.fillText(text2, location.x, text2Y);
    }

    drawMicrophoneMessage({location, size, brightness}: LayoutElementProps) {
        this.ctx.font = `${size}px Roboto Condensed`;
        this.ctx.textAlign = "center";
        this.ctx.textBaseline = "middle";
        this.ctx.fillStyle = `rgb(${255 * brightness} ${255 * brightness} ${255 * brightness})`;
        const text1 = 'PLEASE ACTIVATE';
        const text2 = 'YOUR MICROPHONE';
        const text1Y = location.y - size / 2;
        const text2Y = location.y + size / 2;
        this.ctx.fillText(text1, location.x, text1Y);
        this.ctx.fillText(text2, location.x, text2Y);
    }

    setScrollValue(scrollValue: number) {
        this.scrollValue = scrollValue <= 0 ? 0 : scrollValue >= 1 ? 1 : scrollValue;
    }

    setScrollValueByPixelsFromTop(pixelsFromTop: number) {
        this.setScrollValue(pixelsFromTop / -this.maxScroll);
    }

    getPixelsFromTop() {
        return -this.maxScroll * this.scrollValue;
    }

    addToScrollValue(n: number) {
        n /= - this.maxScroll;
        this.scrollValue = this.scrollValue + n <= 0 ? 0 : this.scrollValue + n >= 1 ? 1 : this.scrollValue + n;
    }

    setScrollValueByPointerLocation(y: number) {
        const props = this.getProps();
        if (props.scroll) {
            this.scrollValue = range(props.scroll.location.y, props.scroll.location.y + props.scroll.size, 0, 1, y);
        }
    }

    changeState(state: StateType, time: number) {
        this.stateChange = true;
        this.state = state;
        this.changeStarted = time;
    }

    setCursorOverride(style: CursorStyle | null) {
        this.cursorOverride = style;
    }

    getProps(time?: number, cursorX?: number, cursorY?: number, message?: LayoutMessage): LayoutProps {
        if (!time) time = 0;
        if (!cursorX) cursorX = 0;
        if (!cursorY) cursorY = 0;
        const width = this.ctx.canvas.width;
        const height = this.ctx.canvas.height;
        let layoutProps: LayoutProps;
        switch(this.layoutType) {
            case 'greetingDesktop':
                layoutProps = this.getGreetingDesktopLayoutProps(width, height, time, cursorX, cursorY);
                break;
            case 'greetingMobile':
                layoutProps = this.getGreetingMobileLayoutProps(width, height, time, cursorX, cursorY);
                break;
            case 'aboutDesktop':
                layoutProps = this.getAboutDesktopLayoutProps(width, height, time, cursorX, cursorY);
                break;
            case 'aboutMobile':
                layoutProps = this.getAboutMobileLayoutProps(width, height, time, cursorX, cursorY);
                break;
            case 'cvDesktop':
                layoutProps = this.getCvDesktopLayoutProps(width, height, time, cursorX, cursorY);
                break;
            case "cvMobile":
                layoutProps = this.getCvMobileLayoutProps(width, height, time, cursorX, cursorY);
                break;
            case "contactsDesktop":
                layoutProps = this.getContactsDesktopLayoutProps(width, height, time, cursorX, cursorY);
                break;
            case "contactsMobile":
                layoutProps = this.getContactsMobileLayoutProps(width, height, time, cursorX, cursorY);
                break;
            case "main":
                layoutProps = this.getMainDesctopLayoutProps(width, height, time, cursorX, cursorY, message)
                break;
        }
        return layoutProps;
    }

    draw(time: number, cursorX: number, cursorY: number, message?: LayoutMessage) {
        this.artist.clear();
        let layoutProps = this.getProps(time, cursorX, cursorY, message);
        this.drawCursor(layoutProps.cursor);
        layoutProps.childComponent.draw && this.drawGreeting(layoutProps.childComponent);
        layoutProps.logo.draw && this.drawLogo(layoutProps.logo);
        layoutProps.hi.draw && this.drawHi(layoutProps.hi);
        layoutProps.photo.draw && this.drawPhoto(layoutProps.photo);
        layoutProps.heart.draw && this.drawHeart(layoutProps.heart);
        layoutProps.peace.draw && this.drawPeace(layoutProps.peace);
        layoutProps.fullScreen.draw && this.drawFullscreen(layoutProps.fullScreen);
        layoutProps.exploreBtn.draw && this.drawExploreBtn(layoutProps.exploreBtn);
        if (layoutProps.social.draw) {
            this.drawLinkedIn(layoutProps.social.linkedIn);
            this.drawTelegram(layoutProps.social.telegram);
            this.drawInstagram(layoutProps.social.instagram);
        } 
        layoutProps.scroll.draw && this.drawScroll(layoutProps.scroll);
        layoutProps.message.draw && this.drawMessage(layoutProps.message);
    }
}